import CommonRBAC from '@kinsta/rbac'
import type { PermissionData } from '@kinsta/rbac'

export class RBAC extends CommonRBAC {
  constructor(permissions: PermissionData[]) {
    super(permissions)
  }

  static createWithPermissions(permissions: Record<string, unknown>[]): RBAC {
    return new RBAC(permissions)
  }
}

export const RoleID = {
  KINSTA_SUPERADMIN_MANAGER: 'kinsta-superadmin-manager',
  KINSTA_SUPERADMIN: 'kinsta-superadmin',
  KINSTA_BETATESTER: 'kinsta-betatester',
  KINSTA_INTERNALTESTER: 'kinsta-internaltester',
  KINSTA_INTERNAL: 'kinsta-internal',
  KINSTA_FINANCE: 'kinsta-finance',
  KINSTA_NOTIFICATION_MANAGER: 'kinsta-notification-manager',
  COMPANY_OWNER: 'company-owner',
  COMPANY_ADMIN: 'company-admin',
  COMPANY_DEVELOPER: 'company-developer',
  COMPANY_BILLING: 'company-billing',
  SITE_ADMIN: 'site-admin',
  SITE_DEVELOPER: 'site-developer',
  APP_ADMIN: 'app-admin',
  DATABASE_ADMIN: 'database-admin',
  STATIC_SITE_ADMIN: 'static-site-admin',
  OBJECT_STORAGE_ADMIN: 'object-storage-admin',
  LOAD_BALANCER_ADMIN: 'load-balancer-admin',
  PROJECT_ADMIN: 'project-admin',
  PROJECT_DEVELOPER: 'project-developer',
}

export const COMPANY_LEVEL_ROLE_IDS = [
  RoleID.COMPANY_OWNER,
  RoleID.COMPANY_ADMIN,
  RoleID.COMPANY_DEVELOPER,
  RoleID.COMPANY_BILLING,
] as const

export const PROJECT_LEVEL_ROLE_IDS = [
  RoleID.PROJECT_ADMIN,
  RoleID.PROJECT_DEVELOPER,
] as const

export const SERVICE_LEVEL_ROLE_IDS = [
  RoleID.APP_ADMIN,
  RoleID.DATABASE_ADMIN,
  RoleID.STATIC_SITE_ADMIN,
  RoleID.OBJECT_STORAGE_ADMIN,
  RoleID.LOAD_BALANCER_ADMIN,
] as const

export const hasProjectLevelRole = (rolesInCompany: readonly string[]) => {
  return PROJECT_LEVEL_ROLE_IDS.some((role) => rolesInCompany.includes(role))
}

export const isCompanyDeveloper = (rolesInCompany: readonly string[]) => {
  return rolesInCompany.includes(RoleID.COMPANY_DEVELOPER)
}

type ScopedCapability = {
  idApp?: string | null
  idDatabase?: string | null
  idLoadBalancer?: string | null
  idObjectStorage?: string | null
  idProject?: string | null
  idRole?: string | null
  idStaticSite?: string | null
  type?: string | null
}

export type CapabilityScope = 'company' | 'project' | 'service' | 'unknown'

const hasCompanyLevelRole = (idRole?: string | null) => {
  return (
    Boolean(idRole) &&
    COMPANY_LEVEL_ROLE_IDS.includes(
      idRole as (typeof COMPANY_LEVEL_ROLE_IDS)[number]
    )
  )
}

const hasProjectLevelRoleId = (idRole?: string | null) => {
  return (
    Boolean(idRole) &&
    PROJECT_LEVEL_ROLE_IDS.includes(
      idRole as (typeof PROJECT_LEVEL_ROLE_IDS)[number]
    )
  )
}

const hasServiceLevelRoleId = (idRole?: string | null) => {
  return (
    Boolean(idRole) &&
    SERVICE_LEVEL_ROLE_IDS.includes(
      idRole as (typeof SERVICE_LEVEL_ROLE_IDS)[number]
    )
  )
}

export const getCapabilityScope = (
  capabilities: readonly ScopedCapability[] = []
): CapabilityScope => {
  if (
    capabilities.some(
      (capability) =>
        capability.type === 'company' || hasCompanyLevelRole(capability.idRole)
    )
  ) {
    return 'company'
  }

  if (
    capabilities.some(
      (capability) =>
        Boolean(capability.idProject) ||
        hasProjectLevelRoleId(capability.idRole)
    )
  ) {
    return 'project'
  }

  if (
    capabilities.some(
      (capability) =>
        Boolean(
          capability.idApp ||
          capability.idDatabase ||
          capability.idStaticSite ||
          capability.idObjectStorage ||
          capability.idLoadBalancer
        ) || hasServiceLevelRoleId(capability.idRole)
    )
  ) {
    return 'service'
  }

  return 'unknown'
}

export const canCompanyDeveloperManageCapabilities = (
  capabilities: readonly ScopedCapability[] = []
) => {
  const scope = getCapabilityScope(capabilities)

  return scope === 'project' || scope === 'service'
}

export const getProjectRoleLabel = (idRole?: string | null) => {
  switch (idRole) {
    case RoleID.PROJECT_ADMIN:
      return 'Project admin'
    case RoleID.PROJECT_DEVELOPER:
      return 'Project developer'
    default:
      return 'Project access'
  }
}

export const getServiceRoleLabel = (idRole?: string | null) => {
  switch (idRole) {
    case RoleID.APP_ADMIN:
      return 'App administrator'
    case RoleID.DATABASE_ADMIN:
      return 'Database administrator'
    case RoleID.STATIC_SITE_ADMIN:
      return 'Static site administrator'
    case RoleID.OBJECT_STORAGE_ADMIN:
      return 'Object storage administrator'
    case RoleID.LOAD_BALANCER_ADMIN:
      return 'Load balancer administrator'
    default:
      return 'Service access'
  }
}

type RbacPermission = {
  can: () => boolean
}

type ProjectUsersRbac = {
  users?: {
    view?: RbacPermission
    update?: RbacPermission
  }
}

export const getProjectUsersAccess = (projectRbac: unknown) => {
  const usersRbac = (projectRbac as ProjectUsersRbac).users

  return {
    canView: usersRbac?.view?.can() ?? false,
    canUpdate: usersRbac?.update?.can() ?? false,
  }
}
